<template>
<div class="w3-row double-list">
  <div class="w3-half">
    <ul v-show="!isApiDomainEditable" class="list-group header">
      <li class="list-group-item input">
        <input @input="resetMarkStart" type="text" v-model="filter" class="form-control" :placeholder="getLang('search')">
        <i @click="resetSearcher" class="fas fa-times reset-searcher"></i>
      </li>
    </ul>
    <ul :class="['list-group list', {'empty': itemsCount === selectedCount}]">
      <transition-group name="fade">
        <li v-for="item in filteredItems" :key="item.value[itemIdKey]" v-if="!item.selected"
          @click="toggleMark($event, item)"
          :class="['list-group-item', 'for-select', {'marked': item.marked}]"
        >
          <span :class="['item-value', 'for-select']">
            {{ getLangValue(item.value) }}
          </span>
        </li>
      </transition-group>
    </ul>
    <div class="button">
      <button @click="add" class="btn btn-simple" :disabled="itemsCount === selectedCount">
        <span>{{ getLang('addSelected') }}</span>
        <i class="fas fa-chevron-right"></i>
      </button>
      <button @click="addAll" class="btn btn-simple" :disabled="itemsCount === selectedCount">
        <span>{{ getLang('addAll') }}</span>
        <i class="fas fa-chevron-right"></i><i class="fas fa-chevron-right"></i>
      </button>
    </div>
  </div>
  <div :class="['w3-half', {'is-api-domain' : isApiDomainEditable}]">
    <ul class="list-group header">
      <li :class="['list-group-item', 'results', {'with-multilanguage': showMultilanguage}]">
        <label v-if="selectedCount > 0">{{ getLang('selected', ['number'], [selectedCount]) }}</label>
        <label v-else>{{ getLang('notSelected') }}</label>
        <div v-if="showMultilanguage" :class="['languageSelector']">
          <definition-multilanguage
            :definition="definition" :object="object" :languageSelected="languageSelected" :translating="false" :language="language" :apiparams="apiparams"
            @newLanguageSelected="newLanguageSelected" @translate="translate" @copy="copy"
          />
        </div>
      </li>
    </ul>
    <ul :class="['list-group list', 'selected-options', {'empty': selectedCount === 0}]">
      <draggable
        v-model="listItems"
        item-key="value"
        :key="0"
        @sort="sortList"
      >
        <template #item="{ element: item }">
          <li v-show="item.selected" :key="item.value[itemIdKey]"
            @click="toggleMark($event, item)"
            :class="['list-group-item', 'for-select', {'marked': item.marked}]"
          >
            <span :key="0" v-show="!item.editing" :class="['for-select', 'item-value', {'item-value-editable': item.value.valueEditable}]">
              {{ getLangValue(item.value) }}
            </span>
            <input :key="1" v-show="item.editing" type="text" :ref="'editValue-'+item.value[itemIdKey]" :class="['item-value', {'item-value-editable': item.value.valueEditable}]"
              v-model="item.editingValue"
            />
            <transition-group name="fade">
              <i :key="0" v-if="item.value.valueEditable && !item.editing"
                class="fas fa-pen"
                v-tooltip.left="{content: getLang('changeText'), delay: { show: 400, hide: 100 }}"
                @click="editValue(item)"
              />
              <template v-else-if="item.value.valueEditable && item.editing">
                <i :key="1"
                  class="sherpa-icon icon-ico_ok"
                  @click="confirmValue(item)"
                />
                <i :key="2"
                  class="sherpa-icon icon-ico_error"
                  @click="cancelValue(item)"
                />
              </template>
            </transition-group>
          </li>
        </template>
      </draggable>
    </ul>
    <div class="button">
      <button @click="removeAll" class="btn btn-simple" :disabled="selectedCount === 0">
        <i class="fas fa-chevron-left"></i><i class="fas fa-chevron-left"></i>
        <span>{{ getLang('deleteAll') }}</span>
      </button>
      <button @click="remove" class="btn btn-simple" :disabled="selectedCount === 0">
        <i class="fas fa-chevron-left"></i>
        <span>{{ getLang('deleteSelected') }}</span>
      </button>
    </div>
  </div>
</div>
</template>

<script>
import language from '../language'
import draggable from 'vuedraggable'
import DefinitionMultilanguage from './multilanguage'

export default {
  name: 'api-list',
  components: {
    draggable,
    DefinitionMultilanguage
  },
  props: [
    'label',
    'items',
    'itemIdKey',
    'value',
    'language',
    'showMultilanguage',
    'definition',
    'object',
    'languageSelected',
    'apiparams',
    'isApiDomainEditable'
  ],
  data () {
    return {
      listItems: [],
      listItemsByKey: {},
      filter: '',
      markStart: null
    }
  },
  mounted () {
    this.init()
  },
  activated () {
    let selected = this.selectedValues
    if (selected.length > 0) {
      this.$emit('input', selected)
    }
  },
  deactivated () {
    this.$emit('input', [])
  },
  watch: {
    items: function () {
      this.init(true)
    }
  },
  computed: {
    selectedValues () {
      return this.listItems.filter(function (item) {
        return item.selected
      }).map(function (item) {
        return item.value
      })
    },
    itemsCount () {
      return this.items.length
    },
    selectedCount () {
      return this.value.length
    },
    filteredItems () {
      if (this.filter.length) {
        return this.listItems.filter(function (item) {
          return this.filterMatch(item)
        }, this)
      } else {
        return this.listItems
      }
    }
  },
  methods: {
    init (fromWatch) {
      let newListItems = []
      let newListItemsByKey = {}
      let newMarkStart = null
      this.items.forEach(function (item, index) {
        let itemKey = (!this.itemIdKey) ? item : item[this.itemIdKey]
        let oldListItemIndex = this.listItemsByKey[itemKey]
        let oldListItem = (oldListItemIndex) ? this.listItems[oldListItemIndex] : false

        newListItems.push({
          value: item,
          index: index,
          marked: oldListItem && oldListItem.marked,
          selected: (oldListItem && oldListItem.selected) || (this.isValueSelected(itemKey)),
          editing: false,
          editingValue: ''
        })

        newListItemsByKey[itemKey] = index

        if (this.markStart && !newMarkStart && oldListItem && oldListItem.index === this.markStart) {
          newMarkStart = index
        }
      }, this)

      // Emit change if selected items are removed
      let removedSelectedItems = []
      if (this.selectedCount > 0) {
        removedSelectedItems = this.selectedValues.filter(function (item) {
          let itemKey = (!this.itemIdKey ? item : item[this.itemIdKey])
          return !newListItemsByKey[itemKey]
        }, this)
      }

      this.markStart = newMarkStart
      this.listItems = newListItems
      this.listItemsByKey = newListItemsByKey

      if (removedSelectedItems.length > 0) {
        if (!fromWatch) {
          this.$emit('input', this.selectedValues)
        }
      }
    },
    isValueSelected (key) {
      var result = false
      if (this.value) {
        for (var i = 0; i < this.value.length && !result; i++) {
          result = (key === this.value[i][this.itemIdKey])
        }
      }
      return result
    },
    filterMatch (item) {
      var text = this.getLangValue(item.value)
      return text.toLowerCase().indexOf(this.filter.toLowerCase()) !== -1
    },
    getLangValue (value) {
      var result = null
      if (this.showMultilanguage) {
        for (var i = 0; i < value.languageValue.length && !result; i++) {
          if (value.languageValue[i].key === this.languageSelected.key) {
            result = value.languageValue[i].value
          }
        }
      } else {
        result = value.value
      }
      return result
    },
    setLangValue (value, text) {
      if (this.showMultilanguage) {
        var founded = false
        for (var i = 0; i < value.languageValue.length && !founded; i++) {
          if (value.languageValue[i].key === this.languageSelected.key) {
            value.languageValue[i].value = text
            founded = true
          }
        }
      } else {
        value.value = text
      }
    },
    moveMarked (e, addToSelected, all) {
      e.preventDefault()
      let items = this.listItems.filter(function (item) {
        return (item.marked || all) && item.selected !== addToSelected
      })
      items.forEach(function (item) {
        item.selected = addToSelected
      })
      this.$emit('input', this.selectedValues)
    },
    add (e) {
      this.moveMarked(e, true)
    },
    addAll (e) {
      this.moveMarked(e, true, true)
    },
    remove (e) {
      this.moveMarked(e, false)
    },
    removeAll (e) {
      this.moveMarked(e, false, true)
    },
    toggleMark (e, item) {
      e = e || window.event

      if (e.srcElement.className.indexOf('for-select') > -1) {
        if (!e.ctrlKey && !(e.shiftKey && this.markStart)) {
          // clear all
          for (let i = 0; i < this.listItems.length; i++) {
            if (this.listItems[i].selected === item.selected && item.value[this.itemIdKey] !== this.listItems[i].value[this.itemIdKey]) {
              this.listItems[i].marked = false
            }
          }
        }

        if (e.shiftKey && this.markStart && this.markStart !== item.index) {
          // mark all from markStart that is not filtered
          let indexStart = Math.min(this.markStart, item.index + 1)
          let indexEnd = Math.max(this.markStart, item.index - 1)
          for (let i = indexStart; i <= indexEnd; i++) {
            if (this.listItems[i].selected === item.selected && this.filterMatch(this.listItems[i])) {
              this.listItems[i].marked = true
            }
          }
        }

        item.marked = !item.marked
        this.markStart = item.marked ? item.index : null
      }
    },
    resetMarkStart (filter) {
      if (this.markStart) {
        let item = this.listItems[this.markStart]
        if (!this.filterMatch(item)) {
          this.markStart = null
        }
      }
    },
    resetSearcher (e) {
      e.preventDefault()
      this.filter = ''
    },
    getLang (key, params, values) {
      return language(this.language, key, params, values)
    },
    sortList (e) {
      this.$emit('input', this.selectedValues)
    },
    editValue (item) {
      item.editing = true
      item.editingValue = this.getLangValue(item.value)
      this.$nextTick(() => {
        this.$refs['editValue-' + item.value[this.itemIdKey]][0].focus()
        this.$refs['editValue-' + item.value[this.itemIdKey]][0].select()
      })
    },
    confirmValue (item) {
      this.setLangValue(item.value, item.editingValue)
      item.editing = false
      this.$emit('input', this.selectedValues)
    },
    cancelValue (item) {
      item.editing = false
    },
    translate (fromLanguage) {
    },
    copy (fromLanguage) {
    },
    newLanguageSelected (language) {
      this.$emit('newLanguageSelected', language)
    }
  }
}
</script>

<style lang="scss">
.double-list {
  .w3-half {
    padding: 0 15px 22px 3px;

    &.is-api-domain {
      margin-top: -32px;
    }

    .list-group {
      margin: 0;
      padding: 0;

      &.list {
        overflow-y: auto;
        user-select: none;
        max-height: 300px;
        min-height: 106px;
      }

      .list-group-item {
        border: 0;
        font-size: 14px;
        font-family: 'Noto Sans';
        background-color: #fff;
        border-top: solid 1px #e0e0e0;
        border-right: solid 1px #e0e0e0;
        border-left: solid 1px #e0e0e0;
        outline: none;
        width: 100%;
        padding: 5px 12px;
        transition: all 0.2s ease-in;
        display: block;
        font-weight: 100;
        color: #666;
        position: relative;

        &:last-child {
          border-bottom: solid 1px #e0e0e0;
        }

        &.input {
          padding: 0;

          input {
            font-size: 12px;
            font-family: 'Noto Sans';
            background-color: transparent;
            border: 1px solid #e0e0e0 !important;
            height: 33px;
            outline: none;
            width: 100%;
            padding: 2px 12px 0;
            transition: all 0.3s ease-in;
            display: block;
            color: #999;

            &:focus {
              background-color: #fff;
            }
          }

          .reset-searcher {
            position: absolute;
            right: 10px;
            top: 11px;
            transition: all 0.2s ease-in;
            color: #999;
            cursor: pointer;
          }
          .reset-searcher:hover {
            color: #666;
          }
        }

        &.results {
          padding: 0;
          border-top: 1px dotted #d0d0d0 !important;
          border-bottom: 1px dotted #d0d0d0 !important;
          border-left: 1px dotted #d0d0d0 !important;
          border-right: 1px dotted #d0d0d0 !important;

          label {
            font-size: 12px;
            font-family: 'Noto Sans';
            border-radius: 3px;
            background-color: transparent;
            outline: none;
            width: 100%;
            padding: 8px 12px 6px;
            transition: all 0.3s ease-in;
            display: block;
            color: #999;
          }

          &.with-multilanguage {
            label {
              float: left;
              width: calc(100% - 108px);
              position: relative;
            }

            .languageSelector {
              float: right;

              .language {
                max-width: 151px;

                .multiselect {
                  float: left;
                  width: auto;

                  .multiselect__tags {
                    width: 105px;
                    border: 0;
                    min-height: 38px;
                  }
                  .multiselect__single {
                    padding-top: 4px !important;
                  }
                  .multiselect__tags,
                  .multiselect__single,
                  .multiselect__input {
                    background-color: #efefef;
                  }
                }

                .translate {
                  display: none;
                }
              }
            }
          }
        }

        &.marked {
          background-color: #dfdfdf;
          color: #333;
          font-weight: 600;
        }

        .item-value {
          font-weight: inherit !important;
        }
      }

      &.selected-options {
        .list-group-item {
          font-weight: 900;
          color: #444;
          cursor: move;

          &.marked {
            span {
              color: #333;
            }
          }

          i {
            position: absolute;
            right: 13px;
            top: 12px;
            cursor: pointer;
            color: #999;
            transition: all 0.2s ease-in;

            &.icon-ico_error {
              color: #df0000;
            }
            &.icon-ico_ok {
              color: #00bb00;
              right: 35px;
            }
          }
          i:hover {
            color: #333;

            &.icon-ico_error {
              color: #ff0000;
            }
            &.icon-ico_ok {
              color: #55eb55;
            }
          }

          input {
            border: 0;
            outline: 0;
            border-bottom: 1px dotted #999;
            font-weight: 100 !important;
            background-color: transparent;
            width: 100%;
            padding-right: 50px;
          }
        }
      }

      &.header {
        margin-bottom: -1px;

        .list-group-item {
          border: 0;
          background-color: transparent;
          color: #333;
          font-weight: 400;
        }
      }
    }

    .button {
      text-align: center;
      margin-top: 10px;
      padding-top: 3px;
      border-top: 1px dotted #e0e0e0;

      button {
        border: 0;
        background-color: #d42e12;
        cursor: pointer;
        font-size: 14px;
        font-family: "Source Sans Pro", sans-serif;
        color: #fff;
        transition: all 0.2s ease-in;
        text-transform: lowercase;
        margin: 0 5px;
        outline: none;
        padding: 3px 10px;
        min-width: 170px;

        &:hover {
          color: #000;
        }
        &:disabled {
          cursor: default;
          background-color: #ccc;
          opacity: 0.5;

          span, i {
            color: #888;
          }
        }
        &:focus {
          border: 0;
        }

        span {
          margin: 0 3px;
        }
      }
    }
  }
}
.doublelist {
  .label.error_validate {
    top: 11px;
    right: 30px;
    left: initial;
  }
}
</style>
